import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useOnboarding, STEP_IDS } from "../../../context/OnboardingContext";
import { useWizardOptions } from "../../../context/WizardOptionsContext";
import { writeB2cPrefs } from "../../../utils/b2c/prefs";
import {
  FRAPPE_URL,
  FRAPPE_API_KEY,
  FRAPPE_API_SECRET,
} from "../../../utils/b2c/constants";
import { BackArrowIcon, CloseIcon, KcLogoSmall } from "../../../utils/b2cIcons";
import ProgressBar from "./ProgressBar";
import DestinationStep from "./DestinationStep";
import DegreeStep from "./DegreeStep";
import FieldOfStudyStep from "./FieldOfStudyStep";
import CityStep from "./CityStep";
import NearestOfficeStep from "./NearestOfficeStep";
import CoachingStep, { COACHING_NOT_INTERESTED } from "./CoachingStep";
import styles from "../../../styles/B2C/OnboardingWizard.module.scss";

const STEP_CONFIG: Record<number, { title: string }> = {
  [STEP_IDS.DESTINATION]: { title: "Select your preferred study destination" },
  [STEP_IDS.DEGREE]: { title: "Select your preferred study level" },
  [STEP_IDS.FIELD_OF_STUDY]: { title: "Select your preferred study area" },
  [STEP_IDS.CITY]: { title: "Which city do you live in?" },
  [STEP_IDS.NEAREST_OFFICE]: { title: "Which office is nearest to you?" },
  [STEP_IDS.COACHING]: { title: "Select your preferred coaching" },
};

interface OnboardingWizardProps {
  onClose?: () => void;
}

const OnboardingWizard: FC<OnboardingWizardProps> = ({ onClose }) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [stepping, setStepping] = useState(false);
  const [finishError, setFinishError] = useState("");
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const {
    currentStep,
    totalSteps,
    activeStepId,
    data,
    enableB2cNearestOffice,
    closeWizard,
    nextStep,
    prevStep,
    updateData,
  } = useOnboarding();
  const wizardOptions = useWizardOptions();

  const goBackOrHome = () => {
    const referrer = typeof window !== "undefined" ? document.referrer : "";
    const cameFromSameOrigin =
      referrer && new URL(referrer).origin === window.location.origin;
    if (cameFromSameOrigin) {
      router.back();
    } else {
      router.push("/");
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      closeWizard();
      if (onClose) {
        onClose();
      } else {
        goBackOrHome();
      }
    } else {
      prevStep();
    }
  };

  const handleFinish = useCallback(async () => {
    setIsLoading(true);
    setFinishError("");

    let counsellorName = "";
    let counsellorEmail = "";
    let counsellorPhone = "";
    let counsellorWhatsapp = "";
    let studentModule = "";
    let partnerUuid = "";

    try {
      const params = new URLSearchParams();
      if (data.city) params.append("student_city", data.city.trim());
      if (data.countries[0])
        params.append("preferred_destination_1", data.countries[0].trim());
      if (data.countries[1])
        params.append("preferred_destination_2", data.countries[1].trim());
      if (data.countries[2])
        params.append("preferred_destination_3", data.countries[2].trim());
      if (data.nearestOffice)
        params.append("nearest_office", data.nearestOffice.trim());

      const response = await fetch(
        `${FRAPPE_URL}/api/method/retrieve_partner_details?${params.toString()}`,
        {
          headers: {
            Authorization: `token ${FRAPPE_API_KEY}:${FRAPPE_API_SECRET}`,
          },
        },
      );
      const result = await response.json();
      const responseData = result?.message?.data;
      if (responseData?.primary_counsellor) {
        counsellorName = responseData.primary_counsellor.full_name || "";
        counsellorEmail = responseData.primary_counsellor.email || "";
        counsellorPhone = responseData.primary_counsellor.phone || "";
        counsellorWhatsapp =
          responseData.primary_counsellor.whatsapp_link || "";
        partnerUuid = (
          responseData.primary_counsellor.counsellor_uuid || ""
        ).trim();
      }
      if (responseData?.student_module) {
        studentModule = responseData.student_module;
      }
    } catch (error) {
      console.error(
        "[OnboardingWizard] Failed to retrieve partner details:",
        error,
      );
    }

    // Strict guard: do not advance with missing partnerUuid — downstream CF/Frappe flow requires it
    if (!partnerUuid) {
      setIsLoading(false);
      setStepping(false);
      setFinishError(
        "We couldn't match you to a counsellor right now. Please check your selections and try again.",
      );
      return;
    }

    // Map selected text preferences → cfIds for search API
    const countryCfIds = data.countries
      .map(
        (name) =>
          wizardOptions.countries.find((country) => country.name === name)
            ?.cfId,
      )
      .filter((id): id is number => id != null);
    // No single cfId represents a unified bucket — expansion happens at the
    // lead-PUT and search boundaries. Field kept for backwards compat with B2cPrefs.
    const degreeLevelCfId = null;
    const studyAreaCfIds = data.studyAreas
      .map(
        (label) =>
          wizardOptions.studyAreas.find(
            (studyArea) => studyArea.label === label,
          )?.cfId,
      )
      .filter((id): id is number => id != null);

    // Step 6 → Frappe: a course maps to 'Yes' + its name; "Not Interested"/empty → 'No'.
    const coachingType = data.coachingType.trim();
    const lookingForCoaching: "Yes" | "No" =
      coachingType && coachingType !== COACHING_NOT_INTERESTED ? "Yes" : "No";
    const preferredCoaching = lookingForCoaching === "Yes" ? coachingType : "";

    const prefs = {
      countries: data.countries
        .map((country) => country.trim())
        .filter(Boolean),
      degreeLevel: data.degreeLevel.trim(),
      studyAreas: data.studyAreas.map((area) => area.trim()).filter(Boolean),
      city: data.city.trim(),
      nearestOffice: data.nearestOffice.trim(),
      coachingType,
      lookingForCoaching,
      preferredCoaching,
      nearestOfficeRequired: enableB2cNearestOffice,
      counsellorName,
      counsellorEmail,
      counsellorPhone,
      counsellorWhatsapp,
      studentModule,
      partnerUuid,
      countryCfIds,
      degreeLevelCfId,
      studyAreaCfIds,
    };
    writeB2cPrefs(prefs);
    closeWizard();

    window.location.replace("/search");
  }, [data, closeWizard, router, wizardOptions, enableB2cNearestOffice]);

  const handleFinishRef = React.useRef(handleFinish);
  handleFinishRef.current = handleFinish;

  const autoAdvance = useCallback(() => {
    setStepping(true);
    setTimeout(() => {
      if (currentStep === totalSteps) {
        handleFinishRef.current();
      } else {
        nextStep();
        setStepping(false);
      }
    }, 400);
  }, [currentStep, totalSteps, nextStep]);

  // Reset scroll to top on every step change — covers auto-advance, back button, and
  // direct jumps. Scroll both the internal wizard body (modal mode) and the window
  // (page mode) so it works regardless of which scroller is active.
  useEffect(() => {
    wizardBodyRef.current?.scrollTo({ top: 0, behavior: "auto" });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [activeStepId]);

  const renderStep = () => {
    switch (activeStepId) {
      case STEP_IDS.DESTINATION:
        return (
          <DestinationStep
            selected={data.countries}
            onSelect={(countries) => {
              updateData({ countries });
              if (countries.length > 0) autoAdvance();
            }}
          />
        );
      case STEP_IDS.DEGREE:
        return (
          <DegreeStep
            selected={data.degreeLevel}
            onSelect={(degreeLevel) => {
              if (!degreeLevel) return;
              updateData({ degreeLevel });
              autoAdvance();
            }}
          />
        );
      case STEP_IDS.FIELD_OF_STUDY:
        return (
          <FieldOfStudyStep
            selected={data.studyAreas}
            onSelect={(studyAreas) => {
              updateData({ studyAreas });
              if (studyAreas.length > 0) autoAdvance();
            }}
          />
        );
      case STEP_IDS.CITY:
        return (
          <CityStep
            selected={data.city}
            onSelect={(city) => {
              if (!city) return;
              updateData({ city });
              autoAdvance();
            }}
          />
        );
      case STEP_IDS.NEAREST_OFFICE:
        return (
          <NearestOfficeStep
            selected={data.nearestOffice}
            onSelect={(nearestOffice) => {
              if (!nearestOffice) return;
              updateData({ nearestOffice });
              autoAdvance();
            }}
          />
        );
      case STEP_IDS.COACHING:
        return (
          <CoachingStep
            selected={data.coachingType}
            onSelect={(coachingType) => {
              if (!coachingType) return;
              updateData({ coachingType });
              autoAdvance();
            }}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.counsellorLoading}>
        {/* <div className={styles.counsellorLoadingInner}>
          <div className={styles.counsellorLoadingText}>Finding your counsellor...</div>
          <div className={styles.counsellorSpinner} />
        </div> */}
      </div>
    );
  }

  if (finishError) {
    return (
      <div className={styles.counsellorLoading}>
        <div className={styles.counsellorLoadingInner}>
          <div className={styles.counsellorLoadingText}>{finishError}</div>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => {
              setFinishError("");
              setStepping(false);
            }}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <style jsx global>{`
        [id^="kiwi-"],
        iframe[id^="kiwi-"] {
          display: none !important;
        }
      `}</style>
      <div className={styles.wizardHeader}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBack}
          aria-label="Go back"
        >
          <BackArrowIcon />
        </button>
        <div className={styles.mobileStepInfo}>
          <h1 className={styles.mobileStepTitle}>
            {STEP_CONFIG[activeStepId]?.title}
          </h1>
          <span className={styles.mobileStepIndicator}>
            Step {currentStep} of {totalSteps}
          </span>
        </div>
        <div className={styles.headerLogo}>
          <KcLogoSmall />
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={() => {
            closeWizard();
            if (onClose) onClose();
            else goBackOrHome();
          }}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
        <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
      </div>

      <div className={styles.wizardCard}>
        <div className={styles.stepHeader}>
          <span className={styles.stepIndicator}>
            Step {currentStep} of {totalSteps}
          </span>
          <h1 className={styles.stepTitle}>
            {STEP_CONFIG[activeStepId]?.title}
          </h1>
        </div>
        <div ref={wizardBodyRef} className={styles.wizardBody}>
          {stepping && <div className={styles.stepOverlay} />}
          <div key={activeStepId} className={styles.stepContent}>
            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;